import { useEffect, useMemo, useState } from "react";
import { useSearchesStore } from "../store/searchesStore";
import { convertException } from "../utils/convertException";
import { IErrorData } from "../services/types";

export const useGetSearches = <T>(
  keyPrefix: string,
  searchText: string,
  fetchFunction: (searchText: string) => Promise<T>
) => {
  const cacheKey = useMemo(() => `${keyPrefix}/${searchText}`, [keyPrefix, searchText]);

  const cachedData = useSearchesStore((state) => state.getSearches<T>(cacheKey));
  const setSearches = useSearchesStore(state => state.setSearches);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<IErrorData | null>(null);

  useEffect(() => {
    if (!searchText || cachedData) {
      return;
    }

    const fetchData = async () => {
      try {
        setIsLoading(true);
        const res = await fetchFunction(searchText);
        setSearches<T>(cacheKey, res);
      } catch (e) {
        setError(convertException(e));
        setSearches(cacheKey, null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [cacheKey, searchText, fetchFunction, cachedData, setSearches]);

  return { data: cachedData, isLoading, error };
};
